import { atom } from "jotai";

import {
  BookingWizardDataAtom,
  CurrentBookingWizardStepAtom,
  CurrentRequestGroupIndexAtom,
  confirmedSlotLockKeyAtom,
  currentDurationOptionsAtom,
} from "../../stores";
import { slotIdsToLockSelectorAtom } from "../slotIdsToLockAtom/slotIdsToLockAtom";

/**
 * Determines if the user can advance to the next step
 *
 * BookingDetails:
 * - DurationBooking: Must have a courseId, durationMins, and startSlotId
 * - GolfBooking: Must have a startSlotId
 * - Other booking type sare unhandled and will require later follow up.
 */
export const canAdvanceToNextStepSelectorAtom = atom((get) => {
  const currentStep = get(CurrentBookingWizardStepAtom);
  const bookingWizardData = get(BookingWizardDataAtom);
  const currentDurationOptions = get(currentDurationOptionsAtom);

  if (
    bookingWizardData?.type === "RequestBooking" &&
    bookingWizardData.maxRequestGroupCount != null &&
    (bookingWizardData.requestGroupCount ?? 1) >
      bookingWizardData.maxRequestGroupCount
  ) {
    return false;
  }

  if (
    (currentStep === "AddPlayers" || currentStep === "AddPlayersPrivate") &&
    bookingWizardData?.type === "RequestBooking" &&
    (bookingWizardData.requestGroupCount ?? 1) > 1
  ) {
    const currentRequestGroupIndex = get(CurrentRequestGroupIndexAtom);
    const currentGroupRounds = bookingWizardData.rounds.filter(
      (round) => (round.bookingIndex ?? 0) === currentRequestGroupIndex
    );
    const hasEligiblePlayer = currentGroupRounds.some((round) =>
      ["customer", "member"].includes(round.player.clientType)
    );
    const hasGuest = currentGroupRounds.some((round) =>
      ["guest", "newGuest"].includes(round.player.clientType)
    );

    return (
      hasEligiblePlayer &&
      (!bookingWizardData.isPrivate ||
        !hasGuest ||
        currentGroupRounds.some(
          (round) => round.player.clientType === "member"
        ))
    );
  }

  if (currentStep === "BookingDetails") {
    if (bookingWizardData?.type === "DurationBooking") {
      const currentGroupSize = bookingWizardData.groupSize ?? null;
      const durationOptionsForSelectedCourse = currentDurationOptions.find(
        (durationOption) =>
          durationOption.courseId === bookingWizardData.courseId
      );
      const hasSelectedValidDuration =
        !!bookingWizardData.durationMins &&
        !!durationOptionsForSelectedCourse?.endSlotOptions.find(
          (durationOption) =>
            durationOption.durationMins === bookingWizardData.durationMins &&
            (currentGroupSize
              ? durationOption.allowedPlayerCounts.includes(currentGroupSize)
              : true)
        );
      const selectedSlotIds = get(slotIdsToLockSelectorAtom);
      const confirmedSlotLockKey = get(confirmedSlotLockKeyAtom);
      const isCurrentSelectionLocked =
        !!selectedSlotIds &&
        selectedSlotIds.split(",").sort().join(",") === confirmedSlotLockKey;

      return !!(
        hasSelectedValidDuration &&
        !!bookingWizardData.courseId &&
        !!bookingWizardData.durationMins &&
        !!bookingWizardData.startSlotId &&
        isCurrentSelectionLocked
      );
    } else if (bookingWizardData?.type === "GolfBooking") {
      return !!bookingWizardData.startSlotId;
    }
  }

  return true;
});
